import { useEffect, useId, useState } from "react";
import { Button, Dialog, Notice } from "../../../../components/ui";
import { businessApi } from "../../../../services/api";
import { WEEKDAYS } from "../scheduleDays";
import { TONE_CLASS, addDays, describeShift, errorText, formatClock, formatDay } from "../myHrText";

export function StatusPill({ label, tone = "muted" }) {
  return (
    <span className={`inline-flex max-w-full items-center rounded-2xl px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${TONE_CLASS[tone] || TONE_CLASS.muted}`}>
      <span className="min-w-0 break-words">{label}</span>
    </span>
  );
}

// The frame every My HR request form opens in (and the HR review forms that reuse it): the shared Dialog, so focus
// is trapped, Escape closes it and focus returns to the control that opened it.
export function ModalFrame({ title, subtitle, onClose, children, footer }) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }} title={title} description={subtitle} footer={footer}>
      {children}
    </Dialog>
  );
}

export const inputClass = `w-full rounded-control border border-default bg-surface px-3 py-2 text-body text-foreground placeholder:text-tertiary hover:border-strong focus:border-accent focus:outline-none focus:ring-2 focus:ring-focus disabled:opacity-50 max-md:min-h-touch`;
export const labelClass = "mb-1 block text-label text-secondary";

export function FormError({ id, message }) {
  if (!message) return null;
  return <Notice id={id} tone="danger" role="alert" className="mt-3">{message}</Notice>;
}

export function Buttons({ onClose, saving, disabled, label }) {
  return (
    <>
      <Button onClick={onClose} disabled={saving}>Cancel</Button>
      <Button type="submit" form="my-hr-request-form" variant="primary" disabled={disabled} loading={saving}>{label}</Button>
    </>
  );
}

/**
 * Ask HR to correct one day's attendance. The employee gives the real clock-in and clock-out
 * times (business-local) and a reason; nothing changes until someone else approves it.
 */
export function CorrectionRequestModal({ day, onClose, onSubmitted }) {
  const record = day?.record || null;
  const shift = day?.schedule?.shift || null;
  const [checkIn, setCheckIn] = useState(record?.checkInLocal || "");
  const [checkOut, setCheckOut] = useState(record?.checkOutLocal || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const errorId = useId();

  const trimmed = reason.trim();
  const valid = checkIn && checkOut && trimmed.length >= 5;
  const overnight = checkIn && checkOut && checkOut <= checkIn;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    try {
      await businessApi.submitAttendanceCorrection({ date: day.date, check_in: checkIn, check_out: checkOut, reason: trimmed });
      onSubmitted?.();
    } catch (err) {
      setError(errorText(err, "Could not send the correction. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalFrame
      title="Request an attendance correction"
      subtitle={`${formatDay(day.date, { year: true })}. Someone else reviews it; your attendance changes only if it is approved.`}
      onClose={onClose}
      footer={<Buttons onClose={onClose} saving={saving} disabled={!valid} label="Send for review" />}
    >
      <form id="my-hr-request-form" onSubmit={submit} className="space-y-4" aria-describedby={error ? errorId : undefined}>
        <dl className="grid grid-cols-1 gap-2 rounded-panel bg-surface-sunken p-3 text-body-sm sm:grid-cols-2">
          <div><dt className="text-secondary">Scheduled</dt><dd className="font-medium">{shift ? describeShift(shift) : "No shift that day"}</dd></div>
          <div><dt className="text-secondary">Recorded now</dt><dd className="font-medium">{record ? `${formatClock(record.checkInLocal)} – ${record.checkOutLocal ? formatClock(record.checkOutLocal) : "no clock-out"}` : "Nothing recorded"}</dd></div>
        </dl>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="correction-in" className={labelClass}>Clock-in time *</label>
            <input id="correction-in" type="time" required value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="correction-out" className={labelClass}>Clock-out time *</label>
            <input id="correction-out" type="time" required value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className={inputClass} />
          </div>
        </div>
        {overnight ? <p className="text-meta text-secondary">The clock-out is earlier than the clock-in, so it counts as the next morning.</p> : null}
        <div>
          <label htmlFor="correction-reason" className={labelClass}>What happened? *</label>
          <textarea id="correction-reason" rows={3} maxLength={500} required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="For example: I forgot to clock out when I left at 5 PM." className={inputClass} />
          <p className="mt-1 text-meta text-tertiary">At least 5 characters. Your reviewer sees this.</p>
        </div>
        <FormError id={errorId} message={error} />
      </form>
    </ModalFrame>
  );
}

/**
 * Ask for a schedule change: a temporary change for some dates (the usual week resumes after)
 * or a new weekly schedule from a date onward. Nothing changes until someone else approves it.
 */
export function ScheduleRequestModal({ today, currentWeek, onClose, onSubmitted }) {
  const [changeType, setChangeType] = useState("temporary");
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(true);
  const tomorrow = addDays(today, 1);
  const [fromDate, setFromDate] = useState(tomorrow);
  const [toDate, setToDate] = useState("");
  const [choice, setChoice] = useState("");
  const [week, setWeek] = useState(() => ({ ...(currentWeek || {}) }));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const errorId = useId();

  useEffect(() => {
    let active = true;
    businessApi.getMyShiftOptions()
      .then((res) => { if (active) setShifts(res?.shifts || []); })
      .catch(() => { if (active) setError("Could not load the shifts. Close and try again."); })
      .finally(() => { if (active) setLoadingShifts(false); });
    return () => { active = false; };
  }, []);

  const trimmed = reason.trim();
  const datesValid = fromDate && fromDate >= tomorrow && (!toDate || toDate >= fromDate);
  const weekValid = WEEKDAYS.every(({ day }) => week[day] !== undefined) && WEEKDAYS.some(({ day }) => week[day]);
  const valid = trimmed.length >= 5 && datesValid && (changeType === "temporary" ? choice !== "" : weekValid);

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError("");
    const payload = changeType === "temporary"
      ? { change_type: "temporary", from_date: fromDate, to_date: toDate || fromDate, reason: trimmed, ...(choice === "rest" ? { rest_day: true } : { shift_id: Number(choice) }) }
      : { change_type: "ongoing", from_date: fromDate, reason: trimmed, weekly_schedule: Object.fromEntries(WEEKDAYS.map(({ day }) => [day, week[day] ? Number(week[day]) : null])) };
    try {
      await businessApi.submitScheduleRequest(payload);
      onSubmitted?.();
    } catch (err) {
      setError(errorText(err, "Could not send the request. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const typeCard = (value, title, text) => (
    <label className={`flex cursor-pointer gap-3 rounded-panel border p-3 text-body-sm text-foreground ${changeType === value ? "border-accent bg-accent-subtle" : "border-default"}`}>
      <input type="radio" name="change-type" value={value} checked={changeType === value} onChange={() => setChangeType(value)} className="mt-1" />
      <span><span className="block font-semibold">{title}</span><span className="text-secondary">{text}</span></span>
    </label>
  );

  return (
    <ModalFrame
      title="Ask for a schedule change"
      subtitle="Someone who manages schedules reviews it. Your schedule changes only if it is approved."
      onClose={onClose}
      footer={<Buttons onClose={onClose} saving={saving} disabled={!valid || loadingShifts} label="Send for review" />}
    >
      <form id="my-hr-request-form" onSubmit={submit} className="space-y-4" aria-describedby={error ? errorId : undefined}>
        <fieldset className="min-w-0 space-y-2">
          <legend className={labelClass}>What kind of change?</legend>
          {typeCard("temporary", "Temporary change", "Different shift or a rest day on certain dates. Your usual week comes back after.")}
          {typeCard("ongoing", "New weekly schedule", "A different shift or rest day for each weekday, from a date onward.")}
        </fieldset>

        <div className="grid grid-cols-2 gap-3">
          <div className={changeType === "ongoing" ? "col-span-2" : ""}>
            <label htmlFor="request-from" className={labelClass}>{changeType === "temporary" ? "First date *" : "Starts on *"}</label>
            <input id="request-from" type="date" required min={tomorrow} value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputClass} />
          </div>
          {changeType === "temporary" ? (
            <div>
              <label htmlFor="request-to" className={labelClass}>Last date</label>
              <input id="request-to" type="date" min={fromDate || tomorrow} value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputClass} />
            </div>
          ) : null}
        </div>
        <p className="-mt-2 text-meta text-tertiary">Changes can start tomorrow at the earliest.{changeType === "temporary" ? " Leave the last date empty for one day (up to 31 days)." : ""}</p>

        {changeType === "temporary" ? (
          <div>
            <label htmlFor="request-shift" className={labelClass}>On those dates *</label>
            <select id="request-shift" required value={choice} onChange={(e) => setChoice(e.target.value)} className={inputClass} disabled={loadingShifts}>
              <option value="">{loadingShifts ? "Loading shifts…" : "Choose a shift or Rest"}</option>
              <option value="rest">Rest day</option>
              {shifts.map((shift) => <option key={shift.id} value={shift.id}>{describeShift(shift)}</option>)}
            </select>
          </div>
        ) : (
          <fieldset className="min-w-0">
            <legend className={labelClass}>Each weekday *</legend>
            <div className="space-y-2">
              {WEEKDAYS.map(({ day, long }) => (
                <div key={day} className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2">
                  <label htmlFor={`week-${day}`} className="text-body-sm text-foreground">{long}</label>
                  <select
                    id={`week-${day}`}
                    value={week[day] === undefined ? "" : (week[day] || "rest")}
                    onChange={(e) => setWeek((current) => ({ ...current, [day]: e.target.value === "rest" ? null : e.target.value === "" ? undefined : Number(e.target.value) }))}
                    className={inputClass}
                    disabled={loadingShifts}
                  >
                    <option value="">Choose…</option>
                    <option value="rest">Rest</option>
                    {shifts.map((shift) => <option key={shift.id} value={shift.id}>{describeShift(shift)}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <p className="mt-1 text-meta text-tertiary">{currentWeek && Object.keys(currentWeek).length ? "Filled in with your current week. " : "Choose a shift or Rest for each day. "}At least one day must be a work day.</p>
          </fieldset>
        )}

        <div>
          <label htmlFor="request-reason" className={labelClass}>Why do you need this? *</label>
          <textarea id="request-reason" rows={3} maxLength={500} required value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} placeholder="For example: I have classes on Monday mornings this term." />
          <p className="mt-1 text-meta text-tertiary">At least 5 characters. Your reviewer sees this.</p>
        </div>
        <FormError id={errorId} message={error} />
      </form>
    </ModalFrame>
  );
}
